import { useEffect, useState, useRef } from 'react';

type UseUploadProgressProps = {
  isUploading: boolean;
  onSuccess: () => void;
  onError?: () => void;
  hasError?: boolean;
};

export const useUploadProgress = ({
  isUploading,
  onSuccess,
  onError,
  hasError = false,
}: UseUploadProgressProps) => {
  const [percentage, setPercentage] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const successFiredRef = useRef(false);

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleSuccess = () => {
    const remainingPercentage = 100 - percentage;
    const steps = 20;
    const incrementPerStep = remainingPercentage / steps;

    let currentStep = 0;
    intervalRef.current = setInterval(() => {
      currentStep += 1;
      // State updaters must stay pure (StrictMode invokes them twice), so the
      // completion side effect runs out here, guarded to fire exactly once.
      setPercentage((prev) => Math.min(prev + incrementPerStep, 100));
      if (currentStep >= steps) {
        clearInterval(intervalRef.current!);
        if (!successFiredRef.current) {
          successFiredRef.current = true;
          onSuccess();
        }
      }
    }, 100);
  };

  const handleError = () => {
    cleanup();
    onError?.();
  };

  useEffect(() => {
    if (isUploading && !hasError) {
      let currentPercentage = 0;
      let decrementFactor = 10;

      const animate = () => {
        if (currentPercentage < 90) {
          const increment = Math.max(1, decrementFactor);
          currentPercentage += increment;
          decrementFactor = Math.max(1, decrementFactor * 0.9);
          setPercentage(currentPercentage);

          timeoutRef.current = setTimeout(
            animate,
            Math.max(200, 1000 * (1 - currentPercentage / 100)),
          );
        }
      };

      animate();
    } else if (!isUploading) {
      if (hasError) {
        handleError();
      } else if (percentage > 0) {
        handleSuccess();
      }
    }

    return cleanup;
  }, [isUploading, hasError]);

  return { percentage };
};
